/* eslint-disable max-lines */
import { Axios } from '@utils/api/axios-instance';
import { POST_URL } from '@utils/api/endpoints';
import { TokenExtension } from '@utils/extensions/token.extension';

// Chunked, resumable upload for sessions whose media lives on S3/OVH.
//
// The S3 cutover (storage.s3_cutover_id) moved session media off Azure, and
// /upload-url answers for those sessions with a single presigned put_object
// URL. One PUT carrying the whole file: no chunking, no resume, and — because
// S3 stages nothing for a plain PUT — not even a trace left behind to tell a
// dropped connection from a signature that was rejected.
//
// The Azure path had already been tuned for slow links (2MB blocks, low
// concurrency) precisely because a single shot never completes on them. The
// cutover silently put every one of those customers back on single-shot. Over
// three days it failed for 8,398 of 8,712 issued upload URLs, while non-direct
// ingest on the same days and the same networks was completing normally.
//
// So this drives the server's multipart endpoints instead: each part is its own
// request, a drop costs one part rather than the file, and an interrupted
// upload can be picked up where it stopped.

// S3 rejects any part but the last below 5MiB, so the server's floor is 5MiB.
// 8MiB keeps a 5GB file well inside the 10,000-part ceiling while still being
// small enough to land between drops on a ~0.5-1 Mbps line (~60-130s per part).
const PART_SIZE = 8 * 1024 * 1024;

// Concurrency does not create bandwidth. On a saturated slow link, N parallel
// parts each advance at 1/N of the rate, so every part stays in flight N times
// longer and is that much more likely to be cut before it lands. Two keeps the
// pipe busy without lengthening any single part's exposure. Same reasoning as
// the Azure path, which settled on the same number.
const CONCURRENCY = 2;

// Per-part retry. The whole point is that a failed part is cheap, so retrying
// it in place beats surfacing an error the user can only answer by starting the
// file over.
const MAX_PART_ATTEMPTS = 4;
const RETRY_BASE_DELAY_MS = 1000;

// Part URLs are signed in batches server-side; ask for the next batch before
// running out so an upload never stalls waiting on a round trip.
const URL_BATCH_SIZE = 200;

const RESUME_KEY_PREFIX = 'scriptix.multipart.';
// Matches the presigned lifetime the API issues (presigned_upload_expiry_hours).
// A resume record older than its URLs is worthless.
const RESUME_TTL_MS = 24 * 60 * 60 * 1000;

interface UploadedPart {
  part_number: number;
  etag: string;
}

interface StartResponse {
  result: {
    upload_id: string;
    session_id: string;
    part_urls: Record<string, string>;
    part_size: number;
    expires_at: string;
    max_file_size: number;
  };
}

interface PartUrlsResponse {
  result: {
    upload_id: string;
    part_urls: Record<string, string>;
    expires_at: string;
  };
}

interface ResumeRecord {
  uploadId: string;
  partSize: number;
  fileSize: number;
  fileName: string;
  lastModified: number;
  parts: UploadedPart[];
  savedAt: number;
}

export interface MultipartUploadOptions {
  sessionId: string;
  file: File;
  onProgress?: (progress: number) => void;
  signal?: AbortSignal;
}

const authHeaders = () => ({
  Authorization: `Bearer ${TokenExtension.get().string}`
});

const sleep = (ms: number) =>
  new Promise(resolve => {
    setTimeout(resolve, ms);
  });

// Keyed by session, not by file: a session is created per file, so this is
// already unique, and it means a resume record cannot be applied to the wrong
// upload. The file identity is stored alongside and re-checked before use.
const resumeKey = (sessionId: string) => `${RESUME_KEY_PREFIX}${sessionId}`;

const readResume = (sessionId: string, file: File): ResumeRecord | null => {
  try {
    const raw = window.localStorage.getItem(resumeKey(sessionId));
    if (!raw) return null;

    const record = JSON.parse(raw) as ResumeRecord;
    const stale = Date.now() - record.savedAt > RESUME_TTL_MS;
    // Same session id but a different file means the user picked something
    // else; resuming would splice two files together.
    const sameFile =
      record.fileSize === file.size &&
      record.fileName === file.name &&
      record.lastModified === file.lastModified;

    if (stale || !sameFile) {
      window.localStorage.removeItem(resumeKey(sessionId));
      return null;
    }
    return record;
  } catch {
    // A corrupt or unavailable store costs us the resume, not the upload.
    return null;
  }
};

const writeResume = (sessionId: string, record: ResumeRecord): void => {
  try {
    window.localStorage.setItem(resumeKey(sessionId), JSON.stringify(record));
  } catch {
    // Private mode / quota. Uploading without resume is still better than
    // failing here.
  }
};

const clearResume = (sessionId: string): void => {
  try {
    window.localStorage.removeItem(resumeKey(sessionId));
  } catch {
    /* nothing to clean up */
  }
};

const startUpload = async (
  sessionId: string,
  file: File
): Promise<StartResponse['result']> => {
  const response = await Axios.post<StartResponse>(
    POST_URL(sessionId).startMultipartUpload,
    {
      file_size: file.size,
      part_size: PART_SIZE,
      part_count: Math.min(Math.ceil(file.size / PART_SIZE), URL_BATCH_SIZE)
    },
    { headers: authHeaders() }
  );
  return response.data.result;
};

const fetchPartUrls = async (
  sessionId: string,
  uploadId: string,
  partNumbers: number[]
): Promise<Record<string, string>> => {
  const response = await Axios.post<PartUrlsResponse>(
    POST_URL(sessionId).multipartPartUrls,
    { upload_id: uploadId, part_numbers: partNumbers },
    { headers: authHeaders() }
  );
  return response.data.result.part_urls;
};

// Committing is the one call whose failure costs the whole transfer, so it is
// retried harder than a part, not less.
//
// Every part already got MAX_PART_ATTEMPTS; this call had exactly one, and a
// single blip on it discarded a file that was completely uploaded. Measured
// server-side over the 90 days to 2026-09-22: 76 sessions, 10.9 GB, all fully
// staged, all asked to upload again. The staged parts stay valid for the
// presigned lifetime, so a retry a minute later still commits the same bytes.
const MAX_COMPLETE_ATTEMPTS = 5;

// S3 commits parts server-side before responding, which on a several-hundred-
// part file takes longer than the 300s default on the shared Axios instance.
// Timing out here is the expensive failure this whole function guards against.
const COMPLETE_TIMEOUT_MS = 10 * 60 * 1000;

const completeUpload = async (
  sessionId: string,
  uploadId: string,
  parts: UploadedPart[]
): Promise<void> => {
  let lastError: unknown;

  for (let attempt = 1; attempt <= MAX_COMPLETE_ATTEMPTS; attempt += 1) {
    try {
      await Axios.post(
        POST_URL(sessionId).completeMultipartUpload,
        { upload_id: uploadId, parts },
        { headers: authHeaders(), timeout: COMPLETE_TIMEOUT_MS }
      );
      return;
    } catch (error) {
      lastError = error;

      // 4xx is a verdict, not a blip: the parts storage holds do not match
      // what we are claiming, and the same body will be rejected identically
      // however many times it is sent. Retrying only delays the error.
      //
      // A replay of an already-committed upload is NOT one of these — the API
      // answers it with the normal success response, because a lost response
      // to a successful commit is exactly what this retry loop creates. See
      // complete_multipart_upload in the API.
      const status = (error as { response?: { status?: number } })?.response
        ?.status;
      if (status && status >= 400 && status < 500) throw error;

      if (attempt < MAX_COMPLETE_ATTEMPTS) {
        await sleep(RETRY_BASE_DELAY_MS * 2 ** (attempt - 1));
      }
    }
  }

  throw lastError;
};

export const abortUpload = async (
  sessionId: string,
  uploadId: string,
  reason?: string
): Promise<void> => {
  try {
    await Axios.post(
      POST_URL(sessionId).abortMultipartUpload,
      { upload_id: uploadId, reason },
      { headers: authHeaders() }
    );
  } catch {
    // Best effort: storage bills for staged parts until they are discarded,
    // but failing to say so must not mask the original error.
  }
};

export const reportUploadFailed = async (
  sessionId: string,
  detail: {
    httpStatus?: number;
    bytesSent?: number;
    fileSize?: number;
    partNumber?: number;
    errorMessage?: string;
  }
): Promise<void> => {
  try {
    await Axios.post(
      POST_URL(sessionId).reportUploadFailed,
      {
        http_status: detail.httpStatus,
        bytes_sent: detail.bytesSent,
        file_size: detail.fileSize,
        part_number: detail.partNumber,
        error_message: detail.errorMessage?.slice(0, 500)
      },
      { headers: authHeaders() }
    );
  } catch {
    // Telemetry. Never the reason an upload reports failure twice.
  }
};

const putPart = (url: string, blob: Blob, signal?: AbortSignal) =>
  new Promise<string>((resolve, reject) => {
    const xhr = new XMLHttpRequest();
    xhr.open('PUT', url, true);

    const onAbort = () => xhr.abort();
    signal?.addEventListener('abort', onAbort);
    const cleanup = () => signal?.removeEventListener('abort', onAbort);

    xhr.onload = () => {
      cleanup();
      if (xhr.status < 200 || xhr.status >= 300) {
        const error = new Error(`Part upload failed (HTTP ${xhr.status})`);
        // Carried so the failure report can distinguish a rejected signature
        // from a dropped connection.
        (error as Error & { httpStatus?: number }).httpStatus = xhr.status;
        reject(error);
        return;
      }
      // S3 returns the part's identity in the ETag header; the commit is
      // rejected if it does not match what storage holds, which is what stops a
      // half-retried part being stitched into the final object.
      const etag = xhr.getResponseHeader('ETag');
      if (!etag) {
        reject(
          new Error(
            'Storage did not return an ETag for this part. If this persists, ' +
              'the bucket may not be exposing ETag to the browser.'
          )
        );
        return;
      }
      resolve(etag);
    };
    xhr.onerror = () => {
      cleanup();
      reject(new Error('Upload failed: the connection was interrupted'));
    };
    xhr.onabort = () => {
      cleanup();
      reject(new Error('Upload cancelled'));
    };

    xhr.send(blob);
  });

/**
 * Upload `file` to S3 in resumable parts, driving the session's multipart
 * endpoints. Resolves once storage has committed the object; the caller still
 * confirms the upload with the session as it does for any other path.
 */
export const uploadInParts = async ({
  sessionId,
  file,
  onProgress,
  signal
}: MultipartUploadOptions): Promise<void> => {
  const resumed = readResume(sessionId, file);

  let uploadId: string;
  let partSize: number;
  let signedUrls: Record<string, string> = {};
  const completed = new Map<number, UploadedPart>();

  if (resumed) {
    uploadId = resumed.uploadId;
    partSize = resumed.partSize;
    resumed.parts.forEach(part => completed.set(part.part_number, part));
  } else {
    const started = await startUpload(sessionId, file);
    uploadId = started.upload_id;
    partSize = started.part_size || PART_SIZE;
    signedUrls = started.part_urls;
  }

  const totalParts = Math.max(1, Math.ceil(file.size / partSize));
  // Parts already staged before this attempt do not need re-sending, which is
  // the entire value of resuming: a customer who lost their connection at 90%
  // sends the last 10%, not the whole file again.
  const pending = Array.from({ length: totalParts }, (_, i) => i + 1).filter(
    partNumber => !completed.has(partNumber)
  );

  let bytesSent = Array.from(completed.values()).reduce(
    (sum, part) =>
      sum + Math.min(partSize, file.size - (part.part_number - 1) * partSize),
    0
  );

  const report = () => {
    if (onProgress) {
      onProgress(Math.min(100, Math.round((bytesSent / file.size) * 100)));
    }
  };
  report();

  const persist = () => {
    writeResume(sessionId, {
      uploadId,
      partSize,
      fileSize: file.size,
      fileName: file.name,
      lastModified: file.lastModified,
      parts: Array.from(completed.values()),
      savedAt: Date.now()
    });
  };

  const urlFor = async (partNumber: number): Promise<string> => {
    if (signedUrls[partNumber]) return signedUrls[partNumber];

    // Ask for a whole batch rather than one URL at a time; a round trip per
    // part would cost more than the part on a high-latency link.
    const batch = pending
      .filter(n => n >= partNumber && !signedUrls[n])
      .slice(0, URL_BATCH_SIZE);
    const fresh = await fetchPartUrls(
      sessionId,
      uploadId,
      batch.length ? batch : [partNumber]
    );
    signedUrls = { ...signedUrls, ...fresh };
    return signedUrls[partNumber];
  };

  const sendPart = async (partNumber: number): Promise<void> => {
    const start = (partNumber - 1) * partSize;
    const blob = file.slice(start, Math.min(start + partSize, file.size));

    let lastError: unknown;
    for (let attempt = 1; attempt <= MAX_PART_ATTEMPTS; attempt += 1) {
      if (signal?.aborted) throw new Error('Upload cancelled');
      try {
        const url = await urlFor(partNumber);

        const etag = await putPart(url, blob, signal);
        completed.set(partNumber, { part_number: partNumber, etag });
        bytesSent += blob.size;
        report();
        persist();
        return;
      } catch (error) {
        lastError = error;
        if (signal?.aborted) throw error;

        // A rejected signature will not fix itself by retrying the same URL, so
        // drop it and re-sign on the next attempt.
        const status = (error as Error & { httpStatus?: number }).httpStatus;
        if (status === 403 || status === 400) {
          delete signedUrls[partNumber];
        }

        if (attempt < MAX_PART_ATTEMPTS) {
          await sleep(RETRY_BASE_DELAY_MS * 2 ** (attempt - 1));
        }
      }
    }
    throw lastError;
  };

  // Fixed-size worker pool over the pending parts.
  const queue = [...pending];
  const worker = async (): Promise<void> => {
    for (;;) {
      const partNumber = queue.shift();
      if (partNumber === undefined) return;

      await sendPart(partNumber);
    }
  };

  try {
    await Promise.all(
      Array.from({ length: Math.min(CONCURRENCY, queue.length || 1) }, worker)
    );

    const parts = Array.from(completed.values()).sort(
      (a, b) => a.part_number - b.part_number
    );
    await completeUpload(sessionId, uploadId, parts);
    clearResume(sessionId);
    if (onProgress) onProgress(100);
  } catch (error) {
    const err = error as Error & { httpStatus?: number };
    await reportUploadFailed(sessionId, {
      httpStatus: err.httpStatus,
      bytesSent,
      fileSize: file.size,
      partNumber: completed.size + 1,
      errorMessage: err.message
    });

    // A cancelled upload is not coming back, so discard the staged parts rather
    // than leaving storage billing for them. A failure might still be resumed,
    // so its parts are deliberately kept.
    if (signal?.aborted) {
      await abortUpload(sessionId, uploadId, 'cancelled by user');
      clearResume(sessionId);
    }
    throw error;
  }
};
